import { computed, effect, isDevMode, resource, Signal, Type } from '@angular/core';

import { resolveRegistryEntry } from '@kros-sk/core/registry';

import { SchemaComponentBaseComponent } from './schema-component-base.component';
import { injectSchemaComponents } from './schema-components.token';

/**
 * Resolves a `componentType` (in an optional `preset`) from the schema-components registry, reactively.
 *
 * Shared by everything that turns a resolver key into a component: `SchemaNodeScopeDirective` — the scope
 * inside `kros-schema-node-outlet` — for one node, {@link injectSchemaRepeatedNode} for a node rendered
 * once per value. They resolve the same key the same way and report the same authoring bug, so a second
 * copy would eventually answer differently for the same mistake.
 *
 * Taking **signals** rather than plain values is what keeps the resolution stable: `params` builds a new
 * object every time it runs, so anything read inside it that changes identity on every read would
 * re-resolve for ever. Signals of strings settle by `Object.is`, so the loader runs again only when the
 * key or the preset actually changes — a node whose `config` was edited keeps the component it had.
 *
 * Call it from an injection context: it creates a `resource` and an `effect`.
 */
export function resolveSchemaComponent(
  componentType: Signal<string | undefined>,
  preset: Signal<string | undefined>
): Signal<Type<SchemaComponentBaseComponent> | null> {
  const registry = injectSchemaComponents();

  const componentResource = resource({
    params: () => ({ componentType: componentType(), preset: preset() }),
    loader: async ({ params }) => {
      if (!params.componentType) {
        return null;
      }
      return resolveRegistryEntry(registry, params.componentType, params.preset);
    },
  });

  effect(() => {
    const failure = componentResource.error();

    if (failure) {
      reportResolveFailure(componentType(), preset(), failure);
    }
  });

  return computed(() => (componentResource.hasValue() ? componentResource.value() : null));
}

/**
 * `getEntry` throws for an unregistered key and the `resource` captures that throw, so without this the
 * node simply fails to appear — nothing in the console, nothing in telemetry, and a schema author left
 * comparing their key against the registry by hand.
 *
 * An unresolvable `componentType` is a schema-authoring bug, never something an end user can act on, so
 * nothing is rendered in its place. Dev gets the actionable warning at the point of failure, the way the
 * registry already reports a duplicate registration; in production this failure is silent.
 */
function reportResolveFailure(componentType: string | undefined, preset: string | undefined, failure: unknown): void {
  if (isDevMode()) {
    const inPreset = preset ? ` in preset "${preset}"` : '';

    console.warn(
      `Schema node "${componentType}"${inPreset} could not be resolved, so nothing is rendered in its place. ` +
        `Register it with addSchemaComponentToRegistry("${componentType}", …) in an injector this node can reach.`,
      failure
    );
  }
}
